import { Workout } from '@/types/workout';
import { SessionProgress } from '@/types/session';

const STORAGE_KEYS = {
  WORKOUTS: 'gym-assistant-workouts',
  SESSION_PROGRESS: 'gym-assistant-session-progress',
  PREFERENCES: 'gym-assistant-preferences',
  WORKOUT_VIEW_MODE: 'gym-assistant-workout-view-mode',
} as const;

export interface StoredData {
  workouts: Workout[];
  sessionProgress: SessionProgress | null;
  preferences: Record<string, unknown>;
}

/**
 * Save a workout to localStorage
 */
export function saveWorkout(workout: Workout): void {
  try {
    const workouts = loadWorkouts();
    const existingIndex = workouts.findIndex(w => w.id === workout.id);
    
    if (existingIndex >= 0) {
      workouts[existingIndex] = workout;
    } else {
      workouts.push(workout);
    }
    
    localStorage.setItem(STORAGE_KEYS.WORKOUTS, JSON.stringify(workouts));
  } catch (error) {
    console.error('Failed to save workout:', error);
  }
}

/**
 * Load all workouts from localStorage
 */
export function loadWorkouts(): Workout[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.WORKOUTS);
    if (!stored) return [];
    
    const workouts = JSON.parse(stored) as Workout[];
    return workouts;
  } catch (error) {
    console.error('Failed to load workouts:', error);
    return [];
  }
}

/**
 * Delete a workout from localStorage
 */
export function deleteWorkout(workoutId: string): void {
  try {
    const workouts = loadWorkouts();
    const filtered = workouts.filter(w => w.id !== workoutId);
    localStorage.setItem(STORAGE_KEYS.WORKOUTS, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to delete workout:', error);
  }
}

/**
 * Save session progress to localStorage
 */
export function saveSessionProgress(progress: SessionProgress | null): void {
  try {
    if (progress) {
      localStorage.setItem(STORAGE_KEYS.SESSION_PROGRESS, JSON.stringify(progress));
    } else {
      localStorage.removeItem(STORAGE_KEYS.SESSION_PROGRESS);
    }
  } catch (error) {
    console.error('Failed to save session progress:', error);
  }
}

/**
 * Load session progress from localStorage
 */
export function loadSessionProgress(): SessionProgress | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SESSION_PROGRESS);
    if (!stored) return null;
    
    return JSON.parse(stored) as SessionProgress;
  } catch (error) {
    console.error('Failed to load session progress:', error);
    return null;
  }
}

/**
 * Save preferences to localStorage
 */
export function savePreferences(preferences: Record<string, unknown>): void {
  try {
    localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(preferences));
  } catch (error) {
    console.error('Failed to save preferences:', error);
  }
}

/**
 * Load preferences from localStorage
 */
export function loadPreferences(): Record<string, unknown> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
    if (!stored) return {};
    
    return JSON.parse(stored) as Record<string, unknown>;
  } catch (error) {
    console.error('Failed to load preferences:', error);
    return {};
  }
}

/**
 * Save workout view mode preference to localStorage
 */
export function saveWorkoutViewMode(mode: 'detailed' | 'compact'): void {
  try {
    localStorage.setItem(STORAGE_KEYS.WORKOUT_VIEW_MODE, mode);
  } catch (error) {
    console.error('Failed to save workout view mode:', error);
  }
}

/**
 * Load workout view mode preference from localStorage
 */
export function loadWorkoutViewMode(): 'detailed' | 'compact' {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.WORKOUT_VIEW_MODE);
    if (stored === 'detailed' || stored === 'compact') {
      return stored;
    }
    return 'detailed'; // Default to detailed view
  } catch (error) {
    console.error('Failed to load workout view mode:', error);
    return 'detailed';
  }
}

/**
 * Clear all stored data
 */
export function clearAllData(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.WORKOUTS);
    localStorage.removeItem(STORAGE_KEYS.SESSION_PROGRESS);
    localStorage.removeItem(STORAGE_KEYS.PREFERENCES);
    localStorage.removeItem(STORAGE_KEYS.WORKOUT_VIEW_MODE);
  } catch (error) {
    console.error('Failed to clear data:', error);
  }
}

